import React, { useState } from "react";
import { Box, Flex, Grid, GridItem, HStack, Spacer, Text, useMediaQuery } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import "../../../App.css";
import APTextInput from "../../../components/APTextInput";
import { useSettingsContext } from "../../../contexts/SettingsContext";
import NavMenu from "../../../components/NavMenu";
import APButton from "../../../components/APButton";
import APIconButton from "../../../components/APIconButton";
import { useDeviceInfo } from "../../../contexts/DeviceContext";
import { TAGS_LIST } from "../../../constants/Scenario";
import { Apple, Autres, Flo, Pen, Play, Science } from "../../../components/APAssetAtlas";
import { IExerciserSDK } from "ap-exerciser-sdk";
import { guestScenariosStorageKey } from "../../../constants/vocabulaire";
import APFooter from "../../../components/APFooter";
import { useVocabIntl } from "../../../hooks/useVocabTranslation";

interface InitialStepProps {
  sdk: IExerciserSDK | undefined
  onStart: (scenarioId: string) => void;
  demo?: string;
  goToListMode: (value: string) => void;
}

export default function InitialStep(props: InitialStepProps) {
  const {
    sdk,
    onStart,
    demo,
    goToListMode,
  } = props;

  const { getFormattedString, APFormattedMessage } = useVocabIntl();

  const { setSettingModalOpen } = useSettingsContext();

  const { width: screenWidth } = useDeviceInfo()
  const [code, setCode] = useState<string>("");
  const [codeError, setCodeError] = useState<boolean>(false);
  const [isLargeScreen] = useMediaQuery("(min-width: 764px)");
  const navigate = useNavigate();

  const profile = (value: string) => {

    if (sdk) {
      sdk.getAnalytics().push({
        event: "select_grade_level",
        action: "click",
        grade_level: value,
        platform: process.env.REACT_APP_PLATFORM
      });
    }

    let url = `/game?value=${value}`;
    if (demo && demo != "") {
      url += "&demo=" + demo;
    }
    navigate(url, { replace: true });
    goToListMode(value);
  };

  const handleOpenSettings = () => {
    setSettingModalOpen(true);
  };

  const goToMyExercises = () => {
    navigate(`/myexercises${demo && "?demo=1"}`, { replace: true });
  };

  const tryLaunchExercise = () => {
    if (code != "") {
      if (code.includes("guest")) {
        if (JSON.parse(sessionStorage.getItem(guestScenariosStorageKey) || "[]").find((scenario: any) => scenario.id === code)) {
          onStart(code);
          return;
        }
      } else if (sdk) {
        sdk.getDataManager().getScenario(code).then(data => {
          if (data) {
            onStart(code);
          } else {
            setCodeError(true)
          }
        })
        return;
      }
    }

    setCodeError(true)
  }

  // @ts-ignore
  return (
    <Flex layerStyle={"scrollPageContainer"}>
      <HStack justifyContent={isLargeScreen ? "space-between" : "center"} layerStyle={"pageHeader"}>

        {screenWidth > 764 && (
          <Text fontSize={"2xl"} fontWeight={900} layerStyle={"textGlow"}>
            <APFormattedMessage id={"app-name"} />
          </Text>
        )}

        <Spacer />

        <NavMenu
          profileBtnTabIndex={1}
          fullscreenBtnTabIndex={2}
          settingsBtnTabIndex={3}
        />

      </HStack>
      <Flex width={"100%"} maxWidth={"container.md"} alignSelf={"center"}>
        <Grid gap={4} maxWidth={"100%"}
              autoFlow={screenWidth > 764 ? "column dense" : "row dense"}
              templateRows={screenWidth > 764 ? "repeat(2, 1fr)" : "repeat(4, auto)"}
              templateColumns={screenWidth > 764 ? "repeat(2, 1fr)" : "repeat(1, 100%)"}>

          {screenWidth < 764 && (
            <GridItem layerStyle={"card"} colSpan={1} rowSpan={1}>
              <Flex layerStyle={"cardContent"}>

                <Box fontSize={"2xl"} textAlign={"center"} fontWeight={"bold"}>
                  <APFormattedMessage id={"interactive-vocabulary-exercise"} />
                </Box>

              </Flex>
            </GridItem>
          )}
          <GridItem colSpan={1} rowSpan={1} layerStyle={"card"}>
            <Flex layerStyle={"cardContent"}>

              <Box fontSize={"lg"} fontWeight={"bold"} layerStyle={"textGlow"}>
                <APFormattedMessage id={"entrer-un-code"} />
              </Box>

              <Box fontSize={"md"} layerStyle={"textGlow"}>
                <APFormattedMessage id={"enter-code-to-start"} />
              </Box>

              <Spacer display={"flex"} flexGrow={1} />

              <HStack fontSize={"2xl"} gap={"1rem"}>
                <APTextInput
                  size={"lg"}
                  maxLength={16}
                  colorScheme={"tertiary"}
                  isInvalid={codeError}
                  placeholder="xxxxxx"
                  tabIndex={4}
                  error={codeError ? getFormattedString("enter-valid-code") : ""}
                  onChange={(e) => {
                    setCode(e.target.value);
                    setCodeError(false)
                  }}
                  value={code}
                  autoCapitalize="none"
                  aria-label={getFormattedString('aria-label-enter-code')}
                />
                <APIconButton
                  variant={"raisedIcon"}
                  colorScheme={"primary"}
                  rounded={"full"}
                  tabIndex={5}
                  onClick={() => tryLaunchExercise()}
                  aria-label={getFormattedString('aria-label-enter-code-submit')}>
                  <Play width={"1em"} height={"1em"} />
                </APIconButton>
              </HStack>

              {/*Background visuals*/}
              <Box bg="lightBlue.400" position={"absolute"} borderRadius={"0 0 0 2rem"}
                   top={0} right={0} width={"5rem"} height={"5rem"} zIndex={-1}
              />
              <Apple preserveAspectRatio={"meet"} position={"absolute"} zIndex={-1}
                     width={"auto"} height={"5rem"} top={0} right={0} transform={"translate(15%,15%)"} />
            </Flex>
          </GridItem>
          <GridItem colSpan={1} rowSpan={1} layerStyle={"card"}>
            <Flex layerStyle={"cardContent"}>

              <Box fontSize={"lg"} fontWeight={"bold"} layerStyle={"textGlow"}>
                <APFormattedMessage id={"my-exercises"} />
              </Box>

              <Box fontSize={"md"} layerStyle={"textGlow"}>
                <APFormattedMessage id={"find-or-create-exercise"} />
              </Box>

              <Spacer minHeight={"8"} />

              <Box display={"flex"} justifyContent={"flex-end"}>
                <Spacer />
                <APButton variant={"raised"} colorScheme={"primary"} tabIndex={6}
                          onClick={goToMyExercises}
                          aria-label={getFormattedString('aria-label-my-exercisers')}>
                  <APFormattedMessage id={"my-exercises-button"}/>
                </APButton>
              </Box>

              {/*Background visuals*/}
              <Box bg="lightBlue.400" position={"absolute"} borderRadius={"0 3.5rem 0 0"}
                   bottom={0} left={0} width={"6rem"} height={"3.5rem"} zIndex={-1}
              />

              <Flo preserveAspectRatio={"meet"} position={"absolute"} zIndex={-1}
                   width={"auto"} height={"6rem"} bottom={0} left={"0.5rem"} />

              <Autres preserveAspectRatio={"meet"} position={"absolute"} zIndex={-1}
                      width={"auto"} height={"5rem"} top={0} right={0} transform={"translate(12%,12%)"} />

              <Pen preserveAspectRatio={"meet"} position={"absolute"} zIndex={-1}
                   height={"8rem"} bottom={0} right={0} />

            </Flex>
          </GridItem>
          <GridItem colSpan={1} rowSpan={2} layerStyle={"card"}>
            <Flex layerStyle={"cardContent"}>

              <Box fontSize={"lg"} fontWeight={"bold"} layerStyle={"textGlow"}>
                <APFormattedMessage id={"exercises-per-year"} />
              </Box>

              <Box fontSize={"md"} layerStyle={"textGlow"}>
                <APFormattedMessage id={"explore-exercises-with-level"}  />
              </Box>
              <div />

              <Grid templateColumns="repeat(2, 1fr)" gap={[2, 4]}>
                {TAGS_LIST.map((tag, index) => (
                  <GridItem alignContent={"center"} key={tag.label}>
                    <APButton flexFill variant={"raised"} colorScheme={"primary"}
                              key={tag.value} tabIndex={7 + index}
                              onClick={() => profile(tag.value)} aria-label={tag.label}>
                      {tag.label}
                    </APButton>
                  </GridItem>
                ))}
              </Grid>

              {/*Background visuals*/}
              <Box bg="lightBlue.400" position={"absolute"} borderRadius={"0 0 0 2rem"}
                   top={0} right={0} width={"5rem"} height={"3.5rem"} zIndex={-1}
              />

              <Science preserveAspectRatio={"meet"} position={"absolute"} zIndex={-1}
                       width={"auto"} height={"5rem"} top={0} right={0} transform={"translate(12%,12%) scale(-1,1)"} />
            </Flex>
          </GridItem>
        </Grid>
      </Flex>
      <APFooter />
    </Flex>
  );
}
